/**
 * Receipt HTTP client.
 */
import api from "./api";

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

function unwrap<T>(res: { data: ApiEnvelope<T> | T }): T {
  const body = res.data as ApiEnvelope<T> | T;
  if (
    body &&
    typeof body === "object" &&
    "data" in (body as Record<string, unknown>) &&
    "success" in (body as Record<string, unknown>)
  ) {
    return (body as ApiEnvelope<T>).data;
  }
  return body as T;
}

export interface ReceiptSendEvent {
  _id: string;
  channel: "email" | "whatsapp" | "print" | "download";
  to: string | null;
  status: "pending" | "sent" | "failed";
  errorMessage: string | null;
  sentAt: string;
}

export interface Receipt {
  _id: string;
  companyId: string;
  businessId: string;
  orderId: string;
  issuedBy: string | null;
  shareToken: string;
  shareExpiresAt: string;
  revoked: boolean;
  revokedAt: string | null;
  publicViewCount: number;
  sends: ReceiptSendEvent[];
  receiptNumber: string;
  totalMinor: number;
  currency: string;
  customerName: string | null;
  customerPhone: string | null;
  customerEmail: string | null;
  createdAt: string;
  updatedAt: string;
}

export const receiptService = {
  /** Create a new receipt for an order. */
  async issue(orderId: string): Promise<Receipt> {
    const res = await api.post(`/receipts/orders/${orderId}`);
    return unwrap<Receipt>(res);
  },

  /** List existing receipts for an order. */
  async listForOrder(orderId: string): Promise<Receipt[]> {
    const res = await api.get(`/receipts/orders/${orderId}`);
    return unwrap<Receipt[]>(res);
  },

  /**
   * Download the PDF as a Blob. Caller can:
   *   - URL.createObjectURL(blob) and trigger a download link
   *   - Render in an <iframe>
   *   - Pipe to print
   */
  async downloadPdf(receiptId: string): Promise<Blob> {
    const res = await api.get(`/receipts/${receiptId}/pdf`, {
      responseType: "blob",
    });
    return res.data as Blob;
  },

  /** Send the receipt via email. */
  async sendEmail(receiptId: string, to: string): Promise<void> {
    await api.post(`/receipts/${receiptId}/send-email`, { to });
  },

  /**
   * Log a send event for non-server-mediated shares (WhatsApp deep link,
   * thermal print confirmation, etc.).
   */
  async recordSend(
    receiptId: string,
    channel: "whatsapp" | "print" | "download",
    to?: string,
  ): Promise<void> {
    await api.post(`/receipts/${receiptId}/record-send`, { channel, to });
  },

  /** Revoke a receipt's public share token (admin). */
  async revoke(receiptId: string): Promise<Receipt> {
    const res = await api.post(`/receipts/${receiptId}/revoke`);
    return unwrap<Receipt>(res);
  },

  /**
   * Build the public-facing URL for sharing.
   *
   * Uses VITE_PUBLIC_APP_URL if set, otherwise location.origin.
   */
  publicUrl(shareToken: string): string {
    const base =
      (import.meta as ImportMeta & { env: Record<string, string> }).env
        ?.VITE_PUBLIC_APP_URL || window.location.origin;
    return `${base}/r/${shareToken}`;
  },

  /**
   * Build a wa.me deep link with a pre-filled message containing the
   * public URL. Clicking opens WhatsApp Web/app with the recipient and
   * message ready to send.
   *
   * Phone format: international, no '+', no spaces. e.g. "233205551234".
   */
  whatsappLink(opts: {
    phone: string;
    shareToken: string;
    businessName?: string;
    receiptNumber?: string;
  }): string {
    const digits = (opts.phone || "").replace(/\D/g, "");
    const message = [
      `Hello! Your receipt${opts.receiptNumber ? ` ${opts.receiptNumber}` : ""}${
        opts.businessName ? ` from ${opts.businessName}` : ""
      } is ready.`,
      "",
      `View it here: ${this.publicUrl(opts.shareToken)}`,
    ].join("\n");
    return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
  },

  // ── Public token endpoints (unauthed) ───────────────────────
  // For the customer-facing /r/:token page

  async getPublicReceipt(token: string): Promise<PublicReceipt> {
    const res = await api.get(`/receipts/public/${token}`, {
      // Don't send auth headers — this is a public token
      headers: { Authorization: "" },
    });
    return unwrap<PublicReceipt>(res);
  },

  /** Direct URL for the public PDF download endpoint. */
  publicPdfUrl(token: string): string {
    const base =
      (import.meta as ImportMeta & { env: Record<string, string> }).env
        ?.VITE_API_BASE_URL || "http://localhost:3000";
    return `${base}/receipts/public/${token}/pdf`;
  },
};

// ── Public view shape (unauthed) ────────────────────────────────

export interface PublicLineItem {
  name: string;
  quantity: number;
  unitPriceMinor: number;
  lineTotalMinor: number;
}

export interface PublicTaxComponent {
  name?: string;
  code?: string;
  rate?: number;
  amountMinor?: number;
  amount?: number;
}

export interface PublicPayment {
  method: string;
  amountMinor: number;
}

export interface PublicReceipt {
  receiptNumber: string;
  issuedAt: string;
  currency: string;
  totalMinor: number;
  customer: { name: string | null };
  business: {
    name: string;
    address?: string;
    phone?: string;
    logoUrl?: string;
    tin?: string | null;
  } | null;
  lineItems: PublicLineItem[];
  taxBreakdown: PublicTaxComponent[];
  subtotalMinor?: number;
  discountMinor?: number;
  payments: PublicPayment[];
  changeMinor?: number;
  /** Still owed on a credit / part-paid sale. */
  balanceMinor?: number;
  amountPaidMinor?: number;
  pdfUrl: string;
}
